import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  DebouncedSearchInput,
  InfoMessage,
  LoadingSpinner,
  useScreenWidth,
} from "@msys/ui";
import { Public as PublicIcon } from "@mui/icons-material";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PimProductTypeSystemKey } from "../../../clients/graphqlTypes.js";
import { LayoutWithLeftColumn } from "../../commons/layout/LayoutWithLeftColumn.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { ProductPreferenceFragment } from "../../features/products/ProductPreferences.generated.js";
import { useProductPreferencesQuery } from "../../features/products/ProductPreferences.generated.js";
import { useBrandPreferences } from "../../features/products/useBrandPreferences.js";
import {
  useProductPreferencesPage_CurrentProductTypeSystemQuery,
  useProductPreferencesPage_ProductTypesQuery,
  useProductPreferencesPage_SearchProductTypesQuery,
  useProductPreferencesPage_TemplateProductTypesQuery,
} from "./ProductPreferences.generated.js";
import {
  GLOBAL_PRODUCT_PREFERENCES_PATH,
  PRODUCT_PREFERENCES_PATH,
  PRODUCT_TYPE_SYSTEM_KEYS,
  PRODUCT_TYPE_SYSTEM_SEARCH_PARAM,
  parseProductTypeSystemKey,
  productTypePreferencesPath,
} from "./productPreferencesPaths.js";
import {
  GlobalBrandPreferences,
  ProductTypePreferences,
} from "./ProductTypePreferences.js";

interface Props {
  submenuItems: PageTopbarItem[];
  /** The Global entry is selected: its static path carries no product type key. */
  selectsGlobal?: boolean;
}

const PRODUCT_TYPE_SEARCH_LIMIT = 50;

// Headroom for the same class key resolving under several product type systems, so the
// title lookup never truncates before every listed key has been answered.
const PRODUCT_TYPE_SYSTEM_TITLE_FACTOR = PRODUCT_TYPE_SYSTEM_KEYS.length;

type ProductTypeRef = {
  productTypeSystemKey: string;
  productTypeKey: string;
};

/** A brand preference entry as the directory counts it: once, under its product type or global. */
export type BrandPreferenceEntry = {
  productType?: ProductTypeRef | null;
};

type PreferenceGroup<B extends BrandPreferenceEntry> = ProductTypeRef & {
  isInert: boolean;
  preferences: ProductPreferenceFragment[];
  brandPreferences: B[];
  preferenceCount: number;
};

/**
 * Groups article and brand preferences by the product type they are keyed to; a global brand
 * entry belongs to no product type. A brand entry counts once, however many lines it names.
 * A preference stored under a product type system other than the current one is inert — PIM
 * looks preferences up by the current system's key, so those rows never rank anything. They
 * are listed all the same, because an entry a user cannot see is an entry they cannot remove.
 */
export function groupPreferencesByProductType<
  B extends BrandPreferenceEntry = BrandPreferenceEntry,
>(
  preferences: readonly ProductPreferenceFragment[],
  currentProductTypeSystemKey: PimProductTypeSystemKey | null,
  brandPreferences: readonly B[] = []
): PreferenceGroup<B>[] {
  const groups = new Map<string, Omit<PreferenceGroup<B>, "preferenceCount">>();

  const groupOf = ({
    productTypeSystemKey,
    productTypeKey,
  }: ProductTypeRef) => {
    const groupKey = entryKey({ productTypeSystemKey, productTypeKey });
    const group = groups.get(groupKey) ?? {
      productTypeSystemKey,
      productTypeKey,
      // Unknown current system: nothing is claimed to be inert rather than marking
      // everything inert on missing information.
      isInert:
        currentProductTypeSystemKey !== null &&
        productTypeSystemKey !== currentProductTypeSystemKey,
      preferences: [],
      brandPreferences: [],
    };
    groups.set(groupKey, group);
    return group;
  };

  for (const preference of preferences) {
    groupOf(preference.productType).preferences.push(preference);
  }
  for (const preference of brandPreferences) {
    if (preference.productType) {
      groupOf(preference.productType).brandPreferences.push(preference);
    }
  }

  return [...groups.values()]
    .map(group => ({
      ...group,
      preferenceCount: group.preferences.length + group.brandPreferences.length,
    }))
    .sort((a, b) => entryKey(a).localeCompare(entryKey(b)));
}

export function countGlobalBrandPreferences(
  brandPreferences: readonly BrandPreferenceEntry[]
): number {
  return brandPreferences.filter(entry => !entry.productType).length;
}

export type ProductTypeDirectoryEntry = {
  productTypeSystemKey: string;
  productTypeKey: string;
  label: string | null;
  preferenceCount: number;
  placeholderCount: number;
  isInert: boolean;
};

function entryKey(entry: {
  productTypeSystemKey: string;
  productTypeKey: string;
}): string {
  return `${entry.productTypeSystemKey}/${entry.productTypeKey}`;
}

/**
 * Product preferences as a directory: a pinned Global entry and the product types on the
 * left, the selected entry's preferences on the right. One screen, and the selection lives in the URL, so a
 * product type stays linkable and browser back walks the selections.
 *
 * The list is one continuous ranking rather than labelled groups — the types the caller has
 * already preferred something for, then the ones their templates actually use, then the rest
 * on demand through the search. Each row's count and marker say which of those it is, which
 * is what group headings would otherwise be for.
 */
export const ProductPreferences = ({
  submenuItems,
  selectsGlobal = false,
}: Props) => {
  const { t } = useTranslate(["Product", "Global"]);
  const client = useApolloClient();
  const navigate = useNavigate();
  const params = useParams<{ productTypeKey: string }>();
  const [searchParams] = useSearchParams();
  const { isMinTablet } = useScreenWidth();

  const [searchTerm, setSearchTerm] = React.useState<string>("");

  const systemParam = searchParams.get(PRODUCT_TYPE_SYSTEM_SEARCH_PARAM);
  const selectedSystemKey = parseProductTypeSystemKey(systemParam);
  // Only an absent system parameter selects the current system; an unknown one selects nothing.
  const selectedKey =
    systemParam !== null && selectedSystemKey === null
      ? null
      : (params.productTypeKey ?? null);

  const preferencesQuery = useProductPreferencesQuery({ client });
  const systemQuery = useProductPreferencesPage_CurrentProductTypeSystemQuery({
    client,
  });
  const templatesQuery = useProductPreferencesPage_TemplateProductTypesQuery({
    client,
  });
  const brandPreferences = useBrandPreferences();

  // Everything reads `data ?? previousData`: a preference write refetches these queries, and
  // falling back to "no data" in between would empty the directory for a frame.
  const preferencesResult = getDataOrNull(
    (preferencesQuery.data ?? preferencesQuery.previousData)
      ?.pimProductPreferences
  );
  const templatesResponse = (templatesQuery.data ?? templatesQuery.previousData)
    ?.templateProductPlaceholderProductTypes;
  // Preferences don't depend on templating: an org without it, or a user who can't read
  // templates, simply has no template product types to list.
  const templatesResult =
    templatesResponse?.__typename === "MissingCapabilities" ||
    templatesResponse?.__typename === "MissingPermissions"
      ? { productTypes: [] }
      : getDataOrNull(templatesResponse);

  const preferences = preferencesResult?.preferences ?? [];
  const systemResult = getDataOrNull(
    (systemQuery.data ?? systemQuery.previousData)?.pimCurrentProductTypeSystem
  );
  const currentProductTypeSystemKey =
    systemResult?.productTypeSystemKey ?? null;
  // A null key is a valid answer (no system configured); a null result is a lookup that has
  // not succeeded, and assigning against it would write under PIM's default system.
  const systemLoading = systemQuery.loading && systemResult === null;
  const systemFailed = !systemQuery.loading && systemResult === null;

  // A URL without a system names the current one; it stays a wildcard only while the current
  // system is unknown, or a key held by two systems would select both rows.
  const resolvedSelectedSystemKey =
    selectedSystemKey ?? currentProductTypeSystemKey;

  const groups = React.useMemo(
    () =>
      groupPreferencesByProductType(
        preferences,
        currentProductTypeSystemKey,
        brandPreferences.brandPreferences
      ),
    [
      preferences,
      currentProductTypeSystemKey,
      brandPreferences.brandPreferences,
    ]
  );

  const globalPreferenceCount = countGlobalBrandPreferences(
    brandPreferences.brandPreferences
  );

  const productTypeKeys = React.useMemo(
    () => [...new Set(groups.map(group => group.productTypeKey))],
    [groups]
  );

  const titlesQuery = useProductPreferencesPage_ProductTypesQuery({
    client,
    // `pimSearchProductTypes(productTypeKeys:)` matches by key across every product type
    // system, and this screen deliberately lists preferences from more than one. A key
    // present in two systems takes two slots, so a limit of one-per-key drops the tail and
    // those rows fall back to showing the raw class key.
    variables: {
      productTypeKeys,
      limit: productTypeKeys.length * PRODUCT_TYPE_SYSTEM_TITLE_FACTOR,
    },
    skip: productTypeKeys.length === 0,
  });

  const titleByKey = React.useMemo(() => {
    const productTypes =
      getDataOrNull(
        (titlesQuery.data ?? titlesQuery.previousData)?.pimSearchProductTypes
      )?.productTypes ?? [];
    return new Map(
      productTypes.map(type => [
        entryKey({
          productTypeSystemKey: type.productTypeSystemKey,
          productTypeKey: type.key,
        }),
        type.label,
      ])
    );
  }, [titlesQuery.data, titlesQuery.previousData]);

  const templateEntries = React.useMemo(
    () =>
      (templatesResult?.productTypes ?? []).map(entry => ({
        productTypeSystemKey: entry.productType.productTypeSystemKey,
        productTypeKey: entry.productType.key,
        label: entry.productType.label,
        placeholderCount: entry.placeholderCount,
      })),
    [templatesResult]
  );

  const placeholderCountByKey = React.useMemo(
    () =>
      new Map(
        templateEntries.map(entry => [entryKey(entry), entry.placeholderCount])
      ),
    [templateEntries]
  );

  const preferred: ProductTypeDirectoryEntry[] = React.useMemo(
    () =>
      groups.map(group => ({
        productTypeSystemKey: group.productTypeSystemKey,
        productTypeKey: group.productTypeKey,
        label: titleByKey.get(entryKey(group)) ?? null,
        preferenceCount: group.preferenceCount,
        placeholderCount: placeholderCountByKey.get(entryKey(group)) ?? 0,
        isInert: group.isInert,
      })),
    [groups, titleByKey, placeholderCountByKey]
  );

  const preferredKeys = React.useMemo(
    () => new Set(preferred.map(entryKey)),
    [preferred]
  );

  // A product type is listed once: a type that is both preferred and used in templates keeps
  // both its counts on the one row.
  const entries: ProductTypeDirectoryEntry[] = React.useMemo(
    () =>
      [
        ...preferred,
        ...templateEntries
          .filter(entry => !preferredKeys.has(entryKey(entry)))
          .map(entry => ({ ...entry, preferenceCount: 0, isInert: false })),
      ].sort(byWorkThenLabel),
    [preferred, templateEntries, preferredKeys]
  );

  const searchQuery = useProductPreferencesPage_SearchProductTypesQuery({
    client,
    variables: { searchTerm, limit: PRODUCT_TYPE_SEARCH_LIMIT },
    skip: searchTerm === "",
  });

  const preferredByKey = React.useMemo(
    () => new Map(preferred.map(entry => [entryKey(entry), entry])),
    [preferred]
  );

  const searchResult = getDataOrNull(
    (searchQuery.data ?? searchQuery.previousData)?.pimSearchProductTypes
  );

  const searchEntries: ProductTypeDirectoryEntry[] = React.useMemo(
    () =>
      (searchResult?.productTypes ?? []).map(productType => {
        const entry = {
          productTypeSystemKey: productType.productTypeSystemKey,
          productTypeKey: productType.key,
        };
        const preferredEntry = preferredByKey.get(entryKey(entry));
        return {
          ...entry,
          label: productType.label,
          preferenceCount: preferredEntry?.preferenceCount ?? 0,
          placeholderCount: placeholderCountByKey.get(entryKey(entry)) ?? 0,
          isInert: preferredEntry?.isInert ?? false,
        };
      }),
    [searchResult, preferredByKey, placeholderCountByKey]
  );

  const searching = searchTerm !== "";
  const listedEntries = searching ? searchEntries : entries;
  // Gated on the query's own loading flag: `getDataOrNull` also yields null for
  // MissingCapabilities/MissingPermissions and for a network error, so testing the result
  // alone leaves a failed load spinning forever instead of reporting itself.
  const entriesLoading =
    (preferencesQuery.loading && preferencesResult === null) ||
    (templatesQuery.loading && templatesResult === null) ||
    systemLoading ||
    brandPreferences.loading;
  const entriesFailed =
    !entriesLoading &&
    (preferencesResult === null ||
      templatesResult === null ||
      systemFailed ||
      brandPreferences.failed);
  const listLoading = searching
    ? searchQuery.loading && searchResult === null
    : entriesLoading;
  const listFailed = searching
    ? !listLoading && searchResult === null
    : entriesFailed;

  const directory = (
    <Stack direction="column" spacing={1} sx={{ minHeight: 0, flex: 1 }}>
      <DebouncedSearchInput
        placeholder={t("Search product types", { ns: "Product" })}
        defaultValue={searchTerm}
        onChangeSearchTerm={setSearchTerm}
        sx={{ px: 1.5 }}
      />
      {/* Pinned above the ranking and never part of it, the search included. */}
      <List dense disablePadding>
        <GlobalDirectoryRow
          preferenceCount={globalPreferenceCount}
          selected={selectsGlobal}
          onSelect={() => navigate(GLOBAL_PRODUCT_PREFERENCES_PATH)}
        />
      </List>
      {listLoading ? (
        <LoadingSpinner />
      ) : listFailed ? (
        <InfoMessage
          message={t("Something went wrong. Please try again.", {
            ns: "Global",
          })}
        />
      ) : listedEntries.length === 0 ? (
        <InfoMessage
          message={
            searching
              ? t("No product types match your search.", { ns: "Product" })
              : t(
                  "No product type is preferred or used in your templates yet. Search for one to get started.",
                  { ns: "Product" }
                )
          }
        />
      ) : (
        <Box sx={{ overflow: "auto", minHeight: 0, flex: 1 }}>
          <List dense disablePadding>
            {listedEntries.map(entry => (
              <ProductTypeDirectoryRow
                key={entryKey(entry)}
                entry={entry}
                selected={
                  entry.productTypeKey === selectedKey &&
                  (resolvedSelectedSystemKey === null ||
                    entry.productTypeSystemKey === resolvedSelectedSystemKey)
                }
                onSelect={() =>
                  navigate(
                    productTypePreferencesPath(
                      entry,
                      currentProductTypeSystemKey
                    )
                  )
                }
              />
            ))}
          </List>
        </Box>
      )}
    </Stack>
  );

  // Nothing selected is not a state worth showing: the ranking already says which product
  // type is most worth working on, so it is opened. Replacing the history entry keeps back
  // from bouncing between the bare path and this selection. Not on mobile, where the list is
  // the screen and a selection would leave the user with no way back to it. The Global entry
  // is not part of the ranking and is never preselected. Only a complete ranking is trusted.
  const firstEntry = entries.at(0) ?? null;
  React.useEffect(() => {
    if (
      !isMinTablet ||
      selectsGlobal ||
      selectedKey !== null ||
      entriesLoading ||
      entriesFailed ||
      firstEntry === null
    )
      return;
    navigate(
      productTypePreferencesPath(firstEntry, currentProductTypeSystemKey),
      {
        replace: true,
      }
    );
  }, [
    isMinTablet,
    selectsGlobal,
    selectedKey,
    entriesLoading,
    entriesFailed,
    firstEntry,
    currentProductTypeSystemKey,
    navigate,
  ]);

  const hasSelection = selectsGlobal || selectedKey !== null;

  const detail = selectsGlobal ? (
    <GlobalBrandPreferences brandPreferences={brandPreferences} />
  ) : selectedKey === null ? null : systemLoading ? (
    <LoadingSpinner />
  ) : systemFailed ? (
    <InfoMessage
      message={t("Something went wrong. Please try again.", {
        ns: "Global",
      })}
    />
  ) : (
    <ProductTypePreferences
      // Remounting on selection keeps no state from the previously selected type.
      key={`${selectedSystemKey ?? ""}/${selectedKey}`}
      productTypeKey={selectedKey}
      productTypeSystemKey={selectedSystemKey}
      currentProductTypeSystemKey={currentProductTypeSystemKey}
      brandPreferences={brandPreferences}
    />
  );

  // Without room for two columns the list *is* the screen until something is selected, and the
  // selection replaces it — the left column would otherwise be unreachable on a phone.
  if (!isMinTablet) {
    return (
      <Page
        title={t("Product preferences", { ns: "Product" })}
        submenuItems={submenuItems}
        {...(hasSelection
          ? {
              backLink: {
                title: t("Product preferences", { ns: "Product" }),
                to: PRODUCT_PREFERENCES_PATH,
              },
            }
          : undefined)}
      >
        <PageContainer>{hasSelection ? detail : directory}</PageContainer>
      </Page>
    );
  }

  return (
    <Page
      title={t("Product preferences", { ns: "Product" })}
      submenuItems={submenuItems}
    >
      <PageContainer>
        <LayoutWithLeftColumn
          name="ProductPreferences"
          leftColumn={directory}
          leftColumnWidth="xs"
        >
          {detail}
        </LayoutWithLeftColumn>
      </PageContainer>
    </Page>
  );
};

/**
 * One continuous ranking: what the caller has already preferred something for first, then what
 * their templates use, then the rest alphabetically.
 */
function byWorkThenLabel(
  a: ProductTypeDirectoryEntry,
  b: ProductTypeDirectoryEntry
): number {
  return (
    b.preferenceCount - a.preferenceCount ||
    b.placeholderCount - a.placeholderCount ||
    (a.label ?? a.productTypeKey).localeCompare(b.label ?? b.productTypeKey)
  );
}

function GlobalDirectoryRow({
  preferenceCount,
  selected,
  onSelect,
}: {
  preferenceCount: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslate(["Product"]);

  return (
    <ListItemButton selected={selected} onClick={onSelect} sx={{ gap: 1 }}>
      <PublicIcon fontSize="small" sx={{ color: "grey.600" }} />
      <ListItemText
        primary={t("Global", { ns: "Product" })}
        slotProps={{
          primary: {
            variant: "body2",
            noWrap: true,
            sx: { fontWeight: selected ? "bold" : undefined },
          },
        }}
      />
      <Typography
        variant="body2"
        sx={{
          color: preferenceCount > 0 ? "text.primary" : "grey.500",
          flexShrink: 0,
        }}
      >
        {preferenceCount}
      </Typography>
    </ListItemButton>
  );
}

function ProductTypeDirectoryRow({
  entry,
  selected,
  onSelect,
}: {
  entry: ProductTypeDirectoryEntry;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslate(["Product", "Global"]);

  const status =
    entry.preferenceCount > 0
      ? entry.isInert
        ? { color: "warning.main", label: t("Not ranking", { ns: "Product" }) }
        : {
            color: "success.main",
            label: t("Has preferences", { ns: "Product" }),
          }
      : entry.placeholderCount > 0
        ? {
            color: "error.main",
            label: t("Used in your templates, nothing preferred yet", {
              ns: "Product",
            }),
          }
        : {
            color: "grey.400",
            label: t("Nothing preferred", { ns: "Product" }),
          };

  return (
    <ListItemButton selected={selected} onClick={onSelect} sx={{ gap: 1 }}>
      {/* The marker carries what group headings would otherwise say: preferred, waiting on
          the templates, or neither. */}
      <Box
        aria-label={status.label}
        title={status.label}
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: status.color,
        }}
      />
      <ListItemText
        primary={entry.label ?? entry.productTypeKey}
        slotProps={{
          primary: {
            variant: "body2",
            noWrap: true,
            sx: { fontWeight: selected ? "bold" : undefined },
          },
        }}
      />
      <Typography
        variant="body2"
        sx={{
          color: entry.preferenceCount > 0 ? "text.primary" : "grey.500",
          flexShrink: 0,
        }}
      >
        {entry.preferenceCount}
      </Typography>
    </ListItemButton>
  );
}
